import { Fragment, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { fmtL, apiErr } from "@/lib/format";
import { toast } from "sonner";
import MultiSelectCombo from "@/components/MultiSelectCombo";
import { XIcon, FileXlsIcon, WarningIcon, CaretDownIcon, CaretRightIcon } from "@phosphor-icons/react";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";
import { getToken } from "@/lib/token";

/**
 * HISTORIAL DE VENTAS — análisis estratégico con las ventas históricas importadas + las facturas nuevas.
 * VALOR FACTURADO = lo vendido en cada operación (no es precio de mercado). VENTA NETA = − notas de crédito.
 */
export const MESES = ["ENERO", "FEBRERO", "MARZO", "ABRIL", "MAYO", "JUNIO", "JULIO", "AGOSTO", "SEPTIEMBRE",
  "OCTUBRE", "NOVIEMBRE", "DICIEMBRE"];
const VISTAS = [
  ["mes-region", "VENTAS POR MES Y REGIÓN"], ["productos", "CONSUMO POR PRODUCTO"], ["clientes", "VENTAS POR CLIENTE"],
  ["precios", "PRECIOS DE VENTA"], ["comparativo", "COMPARATIVO ENTRE AÑOS"], ["detalle", "DETALLE DE OPERACIONES"],
];
const VACIO = { anio: [], mes: [], desde: "", hasta: "", region: [], cliente: [], producto: [] };
const porDefecto = () => ({ ...VACIO, anio: [String(new Date().getFullYear())] });
const LISTAS = ["anio", "mes", "region", "cliente", "producto"];
const ETIQUETAS = { anio: "AÑO", mes: "MES", region: "REGIÓN", cliente: "CLIENTE", producto: "PRODUCTO" };

export function paramsDe(filtros, extra = {}) {
  const p = new URLSearchParams();
  Object.entries(filtros).forEach(([k, v]) => {
    if (Array.isArray(v) ? v.length : v) p.set(k, Array.isArray(v) ? v.join(",") : v);
  });
  Object.entries(extra).forEach(([k, v]) => v !== undefined && v !== "" && v !== null && p.set(k, v));
  return p;
}

const fmtN = (v) => (v == null ? "—" : Number(v).toLocaleString("es-HN", { maximumFractionDigits: 2 }));
const fmtF = (f) => {
  if (!f) return "—";
  const [y, m, d] = String(f).slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
};

export function Celda({ c, v }) {
  if (c.t === "m") return v == null ? "—" : fmtL(v);
  if (c.t === "n") return fmtN(v);
  if (c.t === "p") return v == null ? "—" : <span className={v < 0 ? "text-red-400" : "text-agro-green"}>{fmtN(v)}%</span>;
  if (c.t === "d") return fmtF(v);
  return v == null || v === "" ? "—" : String(v);
}

export default function HistoricalSalesAnalysis() {
  const [sp] = useSearchParams();
  const inicial = useMemo(() => {
    const f = { ...porDefecto() };
    let desdeUrl = false;
    LISTAS.forEach((k) => { if (sp.get(k)) { f[k] = sp.get(k).split(","); desdeUrl = true; } });
    ["desde", "hasta"].forEach((k) => { if (sp.get(k)) { f[k] = sp.get(k); desdeUrl = true; } });
    if (desdeUrl && !sp.get("anio")) f.anio = [];
    return f;
  }, [sp]);
  const [filtros, setFiltros] = useState(inicial);
  const [vista, setVista] = useState(sp.get("vista") || "mes-region");
  const [metrica, setMetrica] = useState("valor");
  const [agrupar, setAgrupar] = useState("region");
  const [ops, setOps] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [drill, setDrill] = useState(null);
  const [abierto, setAbierto] = useState(null);

  useEffect(() => { api.get("/reports/historial-ventas/opciones").then((r) => setOps(r.data)).catch((e) => toast.error(apiErr(e))); }, []);
  const rangoMal = filtros.desde && filtros.hasta && filtros.desde > filtros.hasta;
  const clave = JSON.stringify([filtros, vista, agrupar, vista === "detalle" ? page : 0]);
  useEffect(() => {
    if (rangoMal) return;
    let vivo = true;
    setLoading(true);
    api.get(`/reports/historial-ventas/${vista}?${paramsDe(filtros, { agrupar, page: vista === "detalle" ? page : undefined, page_size: 100 })}`)
      .then((r) => vivo && setData(r.data)).catch((e) => toast.error(apiErr(e))).finally(() => vivo && setLoading(false));
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave]);
  useEffect(() => { setPage(1); setAbierto(null); }, [filtros, vista]);

  const set = (patch) => setFiltros((f) => ({ ...f, ...patch }));
  const nombre = (k, v) => {
    if (k === "mes") return MESES[Number(v) - 1];
    if (k === "cliente") return ops?.clientes.find((c) => c.id === v)?.nombre || v;
    if (k === "producto") return ops?.productos.find((p) => p.id === v)?.nombre || v;
    return v;
  };
  const chips = [];
  LISTAS.forEach((k) => filtros[k].forEach((v) => chips.push({ k, v, l: `${ETIQUETAS[k]}: ${nombre(k, v)}` })));
  if (filtros.desde) chips.push({ k: "desde", l: `DESDE: ${fmtF(filtros.desde)}` });
  if (filtros.hasta) chips.push({ k: "hasta", l: `HASTA: ${fmtF(filtros.hasta)}` });
  const quitar = (c) => (LISTAS.includes(c.k) ? set({ [c.k]: filtros[c.k].filter((x) => x !== c.v) }) : set({ [c.k]: "" }));

  const exportar = async () => {
    try {
      const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
      const res = await fetch(`${apiBase}/reports/historial-ventas/${vista}/export?${paramsDe(filtros, { agrupar })}`, {
        headers: { Authorization: `Bearer ${getToken()}`, "X-Company-Id": localStorage.getItem("agroport_company_id") || "" } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a"); a.href = url; a.download = `historial-ventas-${vista}.xlsx`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error(`No se pudo exportar: ${e.message}`); }
  };

  // UNIDADES ↔ VALOR FACTURADO: muestra la medida elegida (los precios siempre se muestran)
  const columnas = (data?.columns || []).filter((c) => {
    if (vista === "precios" || vista === "detalle") return true;
    if (metrica === "valor") return !(c.t === "u" || c.k === "cantidad" || c.k.startsWith("cant_"));
    return !(c.t === "m" || c.t === "p");
  });
  const filas = data?.rows || [];
  const porPagina = 50;
  const visibles = vista === "detalle" ? filas : filas.slice((page - 1) * porPagina, page * porPagina);
  const paginas = vista === "detalle" ? data?.paginas || 1 : Math.max(Math.ceil(filas.length / porPagina), 1);
  const t = data?.totales;

  return (
    <div className="space-y-4" data-testid="historial-ventas">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading text-lg font-semibold">HISTORIAL DE VENTAS</h2>
          <p className="text-xs text-text-muted max-w-3xl">Ventas históricas importadas y facturas emitidas en el sistema, por fecha de emisión.
            VALOR FACTURADO es lo vendido en cada operación (no es un precio actual de mercado); VENTA NETA descuenta las notas de crédito. Las proformas no son ventas.</p>
        </div>
        <button onClick={exportar} data-testid="hv-exportar" className="px-3 py-2 text-xs border border-border rounded-md flex items-center gap-1.5 hover:border-agro-green">
          <FileXlsIcon size={14} /> EXPORTAR EXCEL
        </button>
      </div>

      {/* FILTROS */}
      <div className="bg-card border border-border/60 rounded-lg p-4 space-y-3" data-testid="hv-filtros">
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
          <MultiSelectCombo label="AÑO" testid="hv-anio" placeholder="TODOS" items={(ops?.anios || []).map(String)} value={filtros.anio} onChange={(v) => set({ anio: v })} />
          <MultiSelectCombo label="MES" testid="hv-mes" placeholder="TODOS" items={MESES.map((m, i) => ({ id: String(i + 1), label: m }))} value={filtros.mes} onChange={(v) => set({ mes: v })} />
          <label className="text-[10px] tracking-wider text-text-muted">DESDE
            <input type="date" value={filtros.desde} max={filtros.hasta || undefined} onChange={(e) => set({ desde: e.target.value })} data-testid="hv-desde"
              className="mt-1 w-full bg-app border border-border rounded-md px-2 py-2 text-sm text-text-primary" /></label>
          <label className="text-[10px] tracking-wider text-text-muted">HASTA
            <input type="date" value={filtros.hasta} min={filtros.desde || undefined} onChange={(e) => set({ hasta: e.target.value })} data-testid="hv-hasta"
              className="mt-1 w-full bg-app border border-border rounded-md px-2 py-2 text-sm text-text-primary" /></label>
          <MultiSelectCombo label="REGIÓN" testid="hv-region" placeholder="TODAS" items={ops?.regiones || []} value={filtros.region} onChange={(v) => set({ region: v })} />
          <MultiSelectCombo label="CLIENTE" testid="hv-cliente" placeholder="TODOS" items={(ops?.clientes || []).map((c) => ({ id: c.id, label: c.nombre }))} value={filtros.cliente} onChange={(v) => set({ cliente: v })} />
          <MultiSelectCombo label="PRODUCTO" testid="hv-producto" placeholder="TODOS"
            items={(ops?.productos || []).map((p) => ({ id: p.id, label: p.nombre, sub: `${p.unidades.join(", ")}${p.historico ? " · SOLO EN HISTORIAL" : ""}` }))}
            value={filtros.producto} onChange={(v) => set({ producto: v })} />
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid="hv-activos">
          <span className="text-[10px] tracking-wider text-text-muted">FILTROS ACTIVOS:</span>
          {chips.length === 0 && <span className="text-[11px] text-text-secondary">NINGUNO — TODO EL HISTORIAL</span>}
          {chips.map((c, i) => (
            <span key={i} className="text-[10px] px-2 py-1 rounded border border-agro-green/30 bg-agro-green/10 text-agro-green flex items-center gap-1">
              {c.l}<button onClick={() => quitar(c)} aria-label="QUITAR"><XIcon size={10} /></button></span>
          ))}
          <div className="ml-auto flex gap-2">
            <button onClick={() => setFiltros(porDefecto())} data-testid="hv-limpiar" className="px-3 py-1.5 text-[11px] font-semibold border border-border rounded-md text-text-secondary hover:text-text-primary">LIMPIAR FILTROS</button>
            <button onClick={() => setFiltros({ ...VACIO })} data-testid="hv-todo" className="px-3 py-1.5 text-[11px] font-semibold border border-agro-green/40 text-agro-green rounded-md">MOSTRAR TODO</button>
          </div>
        </div>
        {rangoMal && <div className="text-xs text-red-400">La fecha DESDE no puede ser posterior a la fecha HASTA.</div>}
      </div>

      {/* TOTALES (todos los resultados del filtro) */}
      {t && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="hv-totales">
          <Kpi l="VALOR FACTURADO" v={fmtL(t.valor)} c="text-agro-green" testid="hv-kpi-valor" />
          <Kpi l="NOTAS DE CRÉDITO" v={fmtL(t.nc)} />
          <Kpi l="VENTA NETA" v={fmtL(t.neto)} />
          <Kpi l="UNIDADES (POR PRESENTACIÓN)" v={t.unidades_txt || "—"} small testid="hv-kpi-unidades" />
          <Kpi l="CLIENTES" v={fmtN(t.clientes)} />
          <Kpi l="OPERACIONES" v={fmtN(t.operaciones)} testid="hv-kpi-operaciones" />
        </div>
      )}
      {t && (t.region_por_cliente > 0 || t.sin_region > 0 || (t.monedas || []).length > 1) && (
        <div className="text-[11px] text-agro-yellow flex items-start gap-1.5" data-testid="hv-avisos">
          <WarningIcon size={14} className="mt-0.5 shrink-0" />
          <span>
            {t.region_por_cliente > 0 && `${t.region_por_cliente} líneas sin región en la venta usan la UBICACIÓN ACTUAL DEL CLIENTE. `}
            {t.sin_region > 0 && `${t.sin_region} líneas aparecen como SIN REGIÓN ASIGNADA. `}
            {(t.monedas || []).length > 1 && `Hay ventas en varias monedas (${t.monedas.join(", ")}): los importes se muestran separados por moneda.`}
          </span>
        </div>
      )}

      {/* VISTAS */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1 border-b border-border/60 flex-wrap flex-1" data-testid="hv-vistas">
          {VISTAS.map(([k, l]) => (
            <button key={k} onClick={() => setVista(k)} data-testid={`hv-vista-${k}`}
              className={`px-3 py-2 text-[11px] font-semibold tracking-wider border-b-2 -mb-px ${vista === k ? "border-agro-green text-agro-green" : "border-transparent text-text-muted hover:text-text-primary"}`}>{l}</button>
          ))}
        </div>
        {vista === "comparativo" && (
          <select value={agrupar} onChange={(e) => setAgrupar(e.target.value)} data-testid="hv-agrupar" className="bg-app border border-border rounded-md px-2 py-1.5 text-xs">
            <option value="region">COMPARAR POR REGIÓN</option><option value="producto">COMPARAR POR PRODUCTO</option>
            <option value="cliente">COMPARAR POR CLIENTE</option><option value="total">TOTAL DEL PERÍODO</option>
          </select>
        )}
        {!["precios", "detalle"].includes(vista) && (
          <div className="inline-flex rounded-md border border-border overflow-hidden" data-testid="hv-metrica">
            {[["valor", "VALOR FACTURADO"], ["unidades", "UNIDADES"]].map(([k, l]) => (
              <button key={k} onClick={() => setMetrica(k)} data-testid={`hv-metrica-${k}`}
                className={`px-3 py-1.5 text-[11px] font-semibold ${metrica === k ? "bg-agro-green/15 text-agro-green" : "text-text-muted"}`}>{l}</button>
            ))}
          </div>
        )}
      </div>

      {vista === "comparativo" && data?.resumen_anual && (
        <div className="bg-card border border-border/60 rounded-lg overflow-x-auto" data-testid="hv-resumen-anual">
          <table className="w-full text-sm">
            <thead className="bg-sidebar"><tr className="text-left text-[10px] tracking-wider text-text-muted">
              <th className="p-2 pl-4">AÑO</th><th className="p-2 text-right">VALOR FACTURADO</th><th className="p-2">UNIDADES</th>
              <th className="p-2 text-right">CLIENTES</th><th className="p-2 text-right">OPERACIONES</th><th className="p-2 text-right">VARIACIÓN</th><th className="p-2 pr-4 text-right">VAR. %</th></tr></thead>
            <tbody>{data.resumen_anual.map((r) => (
              <tr key={r.anio} className="border-t border-border/40">
                <td className="p-2 pl-4 font-semibold">{r.anio}</td><td className="p-2 text-right">{fmtL(r.valor)}</td><td className="p-2 text-xs">{r.unidades_txt || "—"}</td>
                <td className="p-2 text-right">{r.clientes}</td><td className="p-2 text-right">{r.operaciones}</td>
                <td className="p-2 text-right">{r.variacion == null ? "—" : fmtL(r.variacion)}</td>
                <td className="p-2 pr-4 text-right"><Celda c={{ t: "p" }} v={r.variacion_pct} /></td></tr>))}</tbody>
          </table>
        </div>
      )}

      <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="hv-tabla">
            <thead className="bg-sidebar">
              <tr className="text-left text-[10px] tracking-wider text-text-muted">
                {vista === "productos" && <th className="w-6" />}
                {columnas.map((c) => <th key={c.k} className={`p-3 whitespace-nowrap ${["m", "n", "p"].includes(c.t) ? "text-right" : ""}`}>{c.l}</th>)}
              </tr>
            </thead>
            <tbody>
              {loading && !filas.length ? <tr><td colSpan={columnas.length + 1} className="p-8 text-center text-text-muted">CARGANDO…</td></tr>
                : !filas.length ? <tr><td colSpan={columnas.length + 1} className="p-10 text-center text-text-muted" data-testid="hv-vacio">NO HAY VENTAS CON LOS FILTROS SELECCIONADOS</td></tr>
                : visibles.map((r, i) => (
                  <Fragment key={i}>
                    <tr className={`border-t border-border/40 ${r.drill ? "hover:bg-white/[0.03] cursor-pointer" : ""}`}
                      onClick={() => r.drill && setDrill({ titulo: r.producto || r.cliente || r.grupo || `${r.periodo_txt} · ${r.region}`, f: r.drill })}
                      data-testid={`hv-fila-${i}`} title={r.drill ? "VER EL DETALLE DE LAS OPERACIONES" : ""}>
                      {vista === "productos" && (
                        <td className="pl-3"><button onClick={(e) => { e.stopPropagation(); setAbierto(abierto === i ? null : i); }} className="text-text-muted">
                          {abierto === i ? <CaretDownIcon size={12} /> : <CaretRightIcon size={12} />}</button></td>
                      )}
                      {columnas.map((c) => (
                        <td key={c.k} className={`p-3 ${["m", "n", "p"].includes(c.t) ? "text-right whitespace-nowrap" : ""} ${c.t === "u" ? "text-xs" : ""}`}>
                          <Celda c={c} v={r[c.k]} />
                          {c.k === "region" && r.region_por_cliente && <div className="text-[9px] text-agro-yellow">UBICACIÓN ACTUAL DEL CLIENTE</div>}
                          {c.k === "region" && r.region_origen && <div className="text-[9px] text-agro-yellow">{r.region_origen}</div>}
                        </td>
                      ))}
                    </tr>
                    {vista === "productos" && abierto === i && (
                      <tr className="bg-app/40"><td colSpan={columnas.length + 1} className="p-4"><Estacionalidad r={r} /></td></tr>
                    )}
                  </Fragment>
                ))}
            </tbody>
          </table>
        </div>
        {paginas > 1 && (
          <div className="flex justify-end items-center gap-2 px-4 py-2 border-t border-border/50 text-[11px] text-text-muted">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-2 py-1 border border-border rounded disabled:opacity-30">ANTERIOR</button>
            <span>PÁGINA {page} DE {paginas}</span>
            <button disabled={page >= paginas} onClick={() => setPage(page + 1)} data-testid="hv-siguiente" className="px-2 py-1 border border-border rounded disabled:opacity-30">SIGUIENTE</button>
          </div>
        )}
      </div>
      {drill && <DetalleModal titulo={drill.titulo} filtros={{ ...filtros, ...Object.fromEntries(Object.entries(drill.f).map(([k, v]) => [k, k === "periodo" ? v : [v]])) }} onClose={() => setDrill(null)} />}
    </div>
  );
}

function Kpi({ l, v, c = "", small, testid }) {
  return (
    <div className="bg-card border border-border/60 rounded-lg p-3" data-testid={testid}>
      <div className="text-[10px] tracking-wider text-text-muted">{l}</div>
      <div className={`${small ? "text-sm" : "font-heading text-lg"} mt-1 ${c}`}>{v}</div>
    </div>
  );
}

function Estacionalidad({ r }) {
  const vals = Object.values(r.estacionalidad || {});
  const max = Math.max(...vals, 1);
  return (
    <div data-testid="hv-estacionalidad">
      <div className="text-[10px] tracking-wider text-text-muted mb-2">ESTACIONALIDAD — UNIDADES ({r.unidad}) VENDIDAS POR MES, SUMANDO TODOS LOS AÑOS</div>
      <div className="grid grid-cols-12 gap-1 items-end h-28">
        {Object.entries(r.estacionalidad).map(([m, v]) => (
          <div key={m} className="flex flex-col items-center justify-end h-full" title={`${m}: ${fmtN(v)}`}>
            <div className="text-[9px] text-text-muted">{v ? fmtN(v) : ""}</div>
            <div className="w-full bg-agro-green/60 rounded-t" style={{ height: `${(v / max) * 80}%` }} />
            <div className="text-[9px] text-text-muted mt-1">{m.slice(0, 3)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function DetalleModal({ titulo, filtros, onClose }) {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  useEffect(() => {
    const { periodo, ...rest } = filtros;
    api.get(`/reports/historial-ventas/detalle?${paramsDe(rest, { periodo, page, page_size: 100 })}`)
      .then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filtros), page]);
  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);
  const vm = useModalForm({ onClose });
  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...vm.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-6xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="hv-detalle">
        <div className="p-4 border-b border-border flex items-start justify-between">
          <div>
            <div className="text-[10px] tracking-wider text-text-muted">DETALLE DE OPERACIONES</div>
            <h3 className="font-heading text-lg">{titulo}</h3>
            {data && <div className="text-xs text-text-secondary">{data.total_filas} OPERACIONES · VALOR FACTURADO {fmtL(data.totales.valor)} · UNIDADES {data.totales.unidades_txt || "—"}</div>}
          </div>
          <button onClick={onClose} data-testid="hv-detalle-cerrar" aria-label="CERRAR"><XIcon size={18} /></button>
        </div>
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-sidebar sticky top-0"><tr className="text-left text-[10px] tracking-wider text-text-muted">
              {(data?.columns || []).map((c) => <th key={c.k} className={`p-2 whitespace-nowrap ${["m", "n"].includes(c.t) ? "text-right" : ""}`}>{c.l}</th>)}</tr></thead>
            <tbody>{(data?.rows || []).map((r, i) => (
              <tr key={i} className="border-t border-border/40">{data.columns.map((c) => (
                <td key={c.k} className={`p-2 ${["m", "n"].includes(c.t) ? "text-right whitespace-nowrap" : ""}`}><Celda c={c} v={r[c.k]} />
                  {c.k === "region" && r.region_origen && <div className="text-[9px] text-agro-yellow">{r.region_origen}</div>}</td>))}</tr>))}</tbody>
          </table>
        </div>
        {data && data.paginas > 1 && (
          <div className="flex justify-end items-center gap-2 px-4 py-2 border-t border-border/50 text-[11px] text-text-muted">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-2 py-1 border border-border rounded disabled:opacity-30">ANTERIOR</button>
            <span>PÁGINA {data.page} DE {data.paginas}</span>
            <button disabled={page >= data.paginas} onClick={() => setPage(page + 1)} className="px-2 py-1 border border-border rounded disabled:opacity-30">SIGUIENTE</button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
